import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  FileText,
  AlertTriangle,
  TrendingUp,
  Calendar,
  ArrowRight,
  Car,
  Home,
  Briefcase,
  Phone,
  Wifi,
  ShieldPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Asset type configuration with colors and icons
const assetTypeConfig = {
  veicolo: {
    icon: Car,
    label: 'Veicoli',
    color: 'blue',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-600',
    borderColor: 'border-l-blue-500',
    badgeClass: 'bg-blue-100 text-blue-700'
  },
  casa: {
    icon: Home,
    label: 'Case',
    color: 'emerald',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    borderColor: 'border-l-emerald-500',
    badgeClass: 'bg-emerald-100 text-emerald-700'
  },
  attivita: {
    icon: Briefcase,
    label: 'Attività',
    color: 'amber',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-600',
    borderColor: 'border-l-amber-500',
    badgeClass: 'bg-amber-100 text-amber-700'
  },
  altro: {
    icon: FileText,
    label: 'Altro',
    color: 'slate',
    bgColor: 'bg-slate-100',
    textColor: 'text-slate-600',
    borderColor: 'border-l-slate-500',
    badgeClass: 'bg-slate-100 text-slate-700'
  }
};

const contractTypeIcons = { mobile: Phone, fisso: Phone, fibra: Wifi };

const Dashboard = () => {
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });

  const getStatusBadge = (status) => {
    const classes = {
      aperto: 'bg-sky-100 text-sky-700',
      in_lavorazione: 'bg-amber-100 text-amber-700',
      approvato: 'bg-emerald-100 text-emerald-700',
      respinto: 'bg-red-100 text-red-700',
      chiuso: 'bg-slate-100 text-slate-700'
    };
    const labels = { aperto: 'Aperto', in_lavorazione: 'In Lavorazione', approvato: 'Approvato', respinto: 'Respinto', chiuso: 'Chiuso' };
    return <Badge className={`${classes[status] || 'bg-slate-100'}`}>{labels[status] || status}</Badge>;
  };

  // Get icon and config for asset category
  const getAssetConfig = (assetCategory) => {
    return assetTypeConfig[assetCategory] || assetTypeConfig.altro;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-6"><Skeleton className="h-4 w-24 mb-2" /><Skeleton className="h-8 w-16" /></CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard {isAdmin() && <Badge className="ml-2 bg-amber-100 text-amber-700">Admin</Badge>}
          </h1>
          <p className="text-slate-600 mt-1">Panoramica generale del CRM</p>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-slate-200 border-l-4 border-l-blue-500 card-hover" data-testid="stat-clients">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Clienti</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.total_clients || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 border-l-4 border-l-emerald-500 card-hover" data-testid="stat-policies">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Polizze Attive</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.active_policies || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 border-l-4 border-l-violet-500 card-hover" data-testid="stat-contracts">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Contratti Telefonia</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats?.active_contracts || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <Phone className="w-6 h-6 text-violet-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 border-l-4 border-l-sky-500 card-hover" data-testid="stat-premium">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Premi Totali</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{formatCurrency(stats?.total_premium || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-sky-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Secondary Stats - Polizze in Scadenza per Tipologia */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Car className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats?.expiring_by_asset_type?.veicolo || 0}</p>
                <p className="text-sm text-slate-500">Veicoli in Scadenza</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Home className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{stats?.expiring_by_asset_type?.casa || 0}</p>
                <p className="text-sm text-slate-500">Case in Scadenza</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{stats?.expiring_by_asset_type?.attivita || 0}</p>
                <p className="text-sm text-slate-500">Attività in Scadenza</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{stats?.open_claims || 0}</p>
                <p className="text-sm text-slate-500">Sinistri Aperti</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Three Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expiring Policies */}
        <Card className="border-slate-200" data-testid="expiring-policies">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" />Polizze in Scadenza
            </CardTitle>
            <Badge variant="secondary" className="bg-amber-100 text-amber-700">{stats?.expiring_policies_count || 0}</Badge>
          </CardHeader>
          <CardContent>
            {stats?.expiring_policies?.length > 0 ? (
              <div className="space-y-3">
                {stats.expiring_policies.map((policy) => {
                  const config = getAssetConfig(policy.asset_category);
                  const Icon = config.icon;
                  return (
                    <div key={policy.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.bgColor}`}>
                          <Icon className={`w-5 h-5 ${config.textColor}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{policy.client_name}</p>
                          <p className="text-sm text-slate-500">{policy.policy_number}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-amber-600">{formatDate(policy.end_date)}</p>
                        <Badge className={`text-xs ${config.badgeClass}`}>{config.label}</Badge>
                      </div>
                    </div>
                  );
                })}
                <Link to="/renewals">
                  <Button variant="ghost" className="w-full mt-2 text-slate-600 hover:text-slate-900">
                    Vedi tutte <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nessuna polizza in scadenza</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Claims */}
        <Card className="border-slate-200" data-testid="recent-claims">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-slate-600" />Sinistri Recenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.recent_claims?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_claims.map((claim) => (
                  <div key={claim.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div>
                      <p className="font-medium text-slate-900">{claim.client_name}</p>
                      <p className="text-sm text-slate-500 line-clamp-1">{claim.description}</p>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(claim.status)}
                    </div>
                  </div>
                ))}
                <Link to="/claims">
                  <Button variant="ghost" className="w-full mt-2 text-slate-600 hover:text-slate-900">
                    Vedi tutti <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">Nessun sinistro</p>
            )}
          </CardContent>
        </Card>

        {/* Distribution Cards */}
        <div className="space-y-6">
          {/* Policies by Asset Type */}
          <Card className="border-slate-200" data-testid="policies-by-asset-type">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900">Polizze per Tipologia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Veicoli */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Car className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="font-medium text-slate-700">Veicoli</span>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700 font-semibold">{stats?.policies_by_asset_type?.veicolo || 0}</Badge>
                </div>
                
                {/* Case */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Home className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-medium text-slate-700">Case</span>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700 font-semibold">{stats?.policies_by_asset_type?.casa || 0}</Badge>
                </div>
                
                {/* Attività */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="font-medium text-slate-700">Attività</span>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700 font-semibold">{stats?.policies_by_asset_type?.attivita || 0}</Badge>
                </div>
                
                {/* Salute */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-rose-50 hover:bg-rose-100 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center">
                      <ShieldPlus className="w-4 h-4 text-rose-600" />
                    </div>
                    <span className="font-medium text-slate-700">Salute</span>
                  </div>
                  <Badge className="bg-rose-100 text-rose-700 font-semibold">{stats?.policies_by_asset_type?.salute || 0}</Badge>
                </div>
                
                {/* Altro (se presente) */}
                {(stats?.policies_by_asset_type?.altro || 0) > 0 && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <FileText className="w-4 h-4 text-slate-600" />
                      </div>
                      <span className="font-medium text-slate-700">Altro</span>
                    </div>
                    <Badge className="bg-slate-100 text-slate-700 font-semibold">{stats?.policies_by_asset_type?.altro || 0}</Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Contracts by Type */}
          <Card className="border-slate-200" data-testid="contracts-by-type">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900">Contratti per Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(stats?.contracts_by_type || {}).map(([type, count]) => {
                  const Icon = contractTypeIcons[type] || Phone;
                  const labels = { mobile: 'Mobile', fisso: 'Fisso', fibra: 'Fibra', fwa_5g_sim: 'FWA 5G' };
                  return (
                    <div key={type} className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-violet-600" />
                        <span className="text-sm">{labels[type] || type}</span>
                      </div>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
